import React from "react";

import { DeleteOutline } from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useTranslation } from "react-i18next";

import {
  applyTimeType,
  IncidentTimeErrors,
} from "@/components/alerts/add-v2/model/incidentTimes";
import {
  BulletinIncidentTime,
  INCIDENT_TIME_TYPES,
  IncidentTimeType,
} from "@/components/alerts/add-v2/model/types";

import { FIELD_ROW_SX, ROW_DELETE_SX, TYPE_COL_SX } from "./rowLayout";

/**
 * One incident-time row: a heading, the type select, whatever date inputs that type asks for, and the
 * remove button.
 *
 * **The layout is taken from the design prototype's own styles, not inferred** — `rowLayout.ts` holds
 * the parts this shares with `LocationRow`, and the date fields take `flex: 1` each, sharing whatever
 * the type select leaves.
 *
 * Two earlier attempts at this row got it wrong in ways worth naming, because both look fine in a
 * DOM assertion: an equal-thirds grid inherited from the phase 1 placeholder (which clipped a full
 * date and time into a third of a 710px column and left `exact` rows with an empty column), then a
 * fixed-width select plus wrapping date fields (which dropped a range's End onto its own line at the
 * container's left edge, under the select, instead of beside Start). The row does not wrap: the two
 * date fields of a range each take `flex: 1` and split the 70% the select leaves.
 *
 * Which inputs appear per type is the old `IncidentTimeSelector`'s behaviour — see README decision 22
 * for why the rules were ported onto `@mui/x-date-pickers` rather than that component being reused.
 */

/**
 * Same tokens as the issued-date picker in `../../add/AdvancedDetails` — the 2026-07-28 design review
 * asked for date formats to stop differing between this row and the fields above it. Single-spaced
 * where that field triple-spaces, because two of these share a row when the type is a range.
 */
const DATE_TIME_FORMAT = "YYYY-MMM-DD hh:mm A";
const DATE_FORMAT = "YYYY-MMM-DD";

/**
 * `minWidth: 0` on top of the prototype's `flex: 1`, for the reason README decision 17 records about
 * the shell's columns: a flex item's default `min-width: auto` lets its content's min-content width
 * override the basis, so without it a range row overflows instead of splitting.
 */
const DATE_FIELD_SX = { flex: 1, minWidth: 0 };

interface IncidentTimeRowProps {
  value: BulletinIncidentTime;
  /** 1-based, for the heading. Rows are numbered as displayed, never stored. */
  index: number;
  onChange: (next: BulletinIncidentTime) => void;
  onRemove: () => void;
  /** False for the last remaining row — every row that exists is required. */
  removable: boolean;
  /** As `LocationRow`: what is wrong, with the section deciding whether to say so yet. */
  errors: IncidentTimeErrors;
}

export const IncidentTimeRow: React.FC<IncidentTimeRowProps> = ({
  value,
  index,
  onChange,
  onRemove,
  removable,
  errors,
}) => {
  const { t } = useTranslation("bulletin/create-v2");

  // One object per date input rather than one shared: the two halves of a range fail separately.
  const dateTextField = (field: keyof IncidentTimeErrors) => ({
    size: "small" as const,
    required: true,
    sx: DATE_FIELD_SX,
    error: !!errors[field],
    helperText: errors[field]
      ? t(`validation.incident_time_${errors[field]}`)
      : undefined,
  });

  const dateInputs = () => {
    switch (value.timeType) {
      case "unknown":
      case "not_applicable":
        return (
          <TextField
            size="small"
            disabled
            label={t("fields.date_time")}
            value={t("fields.no_date_required")}
            sx={DATE_FIELD_SX}
          />
        );

      case "time_unknown":
        return (
          <DatePicker
            label={t("fields.start_date")}
            format={DATE_FORMAT}
            value={value.startDate}
            disableFuture
            onChange={(startDate) => onChange({ ...value, startDate })}
            slotProps={{ textField: dateTextField("startDate") }}
          />
        );

      case "exact":
        return (
          <DateTimePicker
            label={t("fields.start_date")}
            format={DATE_TIME_FORMAT}
            ampm
            value={value.startDate}
            disableFuture
            onChange={(startDate) => onChange({ ...value, startDate })}
            slotProps={{ textField: dateTextField("startDate") }}
          />
        );

      case "range":
        return (
          <>
            <DateTimePicker
              label={t("fields.start_date")}
              format={DATE_TIME_FORMAT}
              ampm
              value={value.startDate}
              disableFuture
              onChange={(startDate) => onChange({ ...value, startDate })}
              slotProps={{ textField: dateTextField("startDate") }}
            />
            <DateTimePicker
              label={t("fields.end_date")}
              format={DATE_TIME_FORMAT}
              ampm
              value={value.endDate}
              disableFuture
              // An incident cannot end before it started; the start is set first.
              minDateTime={value.startDate ?? undefined}
              onChange={(endDate) => onChange({ ...value, endDate })}
              slotProps={{ textField: dateTextField("endDate") }}
            />
          </>
        );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.4 }}>
        {t("fields.incident_time", { index })}
      </Typography>

      <Box sx={FIELD_ROW_SX}>
        <TextField
          select
          size="small"
          required
          label={t("fields.time_type")}
          value={value.timeType}
          sx={TYPE_COL_SX}
          onChange={(event) =>
            onChange(
              applyTimeType(value, event.target.value as IncidentTimeType),
            )
          }
        >
          {INCIDENT_TIME_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {t(`time_types.${type}`)}
            </MenuItem>
          ))}
        </TextField>

        {dateInputs()}

        <IconButton
          aria-label={t("fields.remove_row")}
          disabled={!removable}
          onClick={onRemove}
          sx={ROW_DELETE_SX}
        >
          <DeleteOutline />
        </IconButton>
      </Box>
    </Box>
  );
};
